import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { ListRow } from "components/designV2/listRow/listRow";
import { MetricCell } from "components/designV2/metricCell/metricCell";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";
import { LocalDate } from "@js-joda/core";
import { Pagination } from "../general/pagination.component";
import { AbsenceDetailRow } from "./absenceDetailRow.component";
import { PayoutDetailRow } from "./payoutDetailRow.component";
import React from "react";
import { workersAGI } from "model/Salary";
import {
  ClickableRow,
  ExpandedSection,
  ListContainer,
  RowOuter,
} from "./agiList.styled";

interface AGIListProps {
  currentDate: LocalDate;
  data: workersAGI[];
  setData: React.Dispatch<React.SetStateAction<workersAGI[]>>;
}

export interface DetailsState {
  isOpen: boolean;
  selectedWorkerId: number | null;
}

export const AGIList: React.FC<AGIListProps> = (props) => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages, workerIdToExternalId }] =
    useCompanyStore();
  const itemsPerPage = 25;
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [allWorkers, setAllWorkers] = useState<workersAGI[]>([]);
  const [detailsState, setDetailsState] = useState<DetailsState>({
    isOpen: false,
    selectedWorkerId: null,
  });
  const currency = " SEK";

  const fetchAGIData = useCallback(() => {
    if (!company?.id) return;

    Api()
      .company.salary.getWorkersAGI(
        company.id,
        props.currentDate.year(),
        props.currentDate.monthValue(),
      )
      .then((res) => {
        setAllWorkers(res.data.workers);
        setTotalPages(Math.ceil(res.data.workers.length / itemsPerPage));
        props.setData(res.data.workers.slice(0, itemsPerPage));
      })
      .catch((e: AxiosError) => {
        console.error("Error when getting individual agi", e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, props.currentDate, props.setData, itemsPerPage]);

  const handlePageChange = useCallback(
    (newPageOrUpdater: React.SetStateAction<number>) => {
      const newPage =
        typeof newPageOrUpdater === "function"
          ? newPageOrUpdater(page)
          : newPageOrUpdater;

      setPage(newPage);
      const startIndex = newPage * itemsPerPage;
      props.setData(allWorkers.slice(startIndex, startIndex + itemsPerPage));
      setDetailsState({ isOpen: false, selectedWorkerId: null });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allWorkers, itemsPerPage, props.setData, page],
  );

  const toggleDetails = useCallback((workerId: number) => {
    setDetailsState((prev) => ({
      isOpen: prev.selectedWorkerId === workerId ? !prev.isOpen : true,
      selectedWorkerId: workerId,
    }));
  }, []);

  const isDetailsVisible = useCallback(
    (workerId: number) =>
      detailsState.isOpen && detailsState.selectedWorkerId === workerId,
    [detailsState],
  );

  useEffect(() => {
    fetchAGIData();
  }, [fetchAGIData]);

  const renderAGIRow = useCallback(
    (agi: workersAGI) => {
      const detailsOpen = isDetailsVisible(agi.workerId);
      const externalId = workerIdToExternalId[agi.workerId];
      const avatarUrl = externalId
        ? (companyWorkerImages[externalId] ?? null)
        : null;

      return (
        <ListRow key={`agi-worker-${agi.workerId}`}>
          <RowOuter>
            <ClickableRow onClick={() => toggleDetails(agi.workerId)}>
              <AvatarChip
                title={`${agi.firstName} ${agi.lastName}`}
                avatarUrl={avatarUrl}
              />
              <MetricCell
                value={agi.personalIdentityNumber}
                label={t("Salary.PersonalIdentityNumber")}
              />
              <MetricCell
                value={`${agi.totalGrossPayoutAmount}${currency}`}
                label={t("Salary.GrossPayoutAmount")}
              />
              <MetricCell
                value={`${agi.totalEmployerTax}${currency}`}
                label={t("Salary.EmployerTax")}
              />
              <MetricCell
                value={`${agi.totalWorkerTax}${currency}`}
                label={t("Salary.Tax")}
              />
              <CustomIcon
                name={detailsOpen ? "v2/up-chevron" : "v2/down-chevron"}
                size="20px"
                color={ColorV2.SimflexityGreen}
              />
            </ClickableRow>

            {detailsOpen && (
              <ExpandedSection>
                <PayoutDetailRow
                  payoutDetails={agi.payoutDetails}
                  currency="SEK"
                />
                {agi.absences.length > 0 && (
                  <AbsenceDetailRow absences={agi.absences} />
                )}
              </ExpandedSection>
            )}
          </RowOuter>
        </ListRow>
      );
    },
    [
      t,
      currency,
      toggleDetails,
      isDetailsVisible,
      companyWorkerImages,
      workerIdToExternalId,
    ],
  );

  return (
    <ListContainer>
      {props.data.map(renderAGIRow)}
      {totalPages > 1 && (
        <Pagination
          setPage={handlePageChange}
          page={page}
          totalPages={totalPages}
        />
      )}
    </ListContainer>
  );
};
